import type { SortingState } from '@tanstack/react-table';
import {
  SORTABLE_REPORTER_TEMPLATES_AND_MACROS_COLUMN_MAP,
  SORTABLE_REPORTER_TEMPLATES_AND_MACROS_COLUMN_MAP_REVERSED,
} from 'config/constants';

/**
 * Convert a sorting state to an API order param string.
 * Example: [{id:'lastUsed',desc:true}{id:'name',desc:false}]
 *   -> "-last_used_datetime,name"
 */
export const sortingStateToOrderString = (
  sorting: SortingState,
  fallback?: string
): string | undefined => {
  if (!sorting || sorting.length === 0) return fallback;

  const seen = new Set<string>();
  const clauses = sorting
    .filter(({ id }) => {
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .map(({ id, desc }) => {
      const apiColumn =
        SORTABLE_REPORTER_TEMPLATES_AND_MACROS_COLUMN_MAP[
          id as keyof typeof SORTABLE_REPORTER_TEMPLATES_AND_MACROS_COLUMN_MAP
        ];
      if (!apiColumn) return null;
      return `${desc ? '-' : ''}${apiColumn}`;
    })
    .filter((clause): clause is string => clause != null);

  return clauses.length > 0 ? clauses.join(',') : fallback;
};

/**
 * Convert an API order param string into a sorting state.
 * Supports "col" (asc) or "-col" (desc).
 *
 * Example: "-last_used_datetime,name"
 *   -> [{id:'lastUsed',desc:true},{id:'name',desc:false}]
 */
export const orderStringToSortingState = (
  orderParam: Nullable<string>,
  options?: { includeUnknown?: boolean }
): SortingState => {
  if (!orderParam) return [];

  const { includeUnknown = false } = options ?? {};
  const pieces = orderParam
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const seen = new Set<string>();
  const state: SortingState = [];

  for (const piece of pieces) {
    const desc = piece.startsWith('-');
    const token = desc ? piece.slice(1) : piece;

    const columnId =
      SORTABLE_REPORTER_TEMPLATES_AND_MACROS_COLUMN_MAP_REVERSED[
        token as keyof typeof SORTABLE_REPORTER_TEMPLATES_AND_MACROS_COLUMN_MAP_REVERSED
      ];

    if (columnId) {
      if (seen.has(columnId)) continue;
      seen.add(columnId);
      state.push({ id: columnId, desc });
    } else if (includeUnknown) {
      if (seen.has(token)) continue;
      seen.add(token);
      state.push({ id: token, desc });
    }
  }

  return state;
};
